/**
 * The cost export (Feature 69): one Export button on each cost view, a
 * dropdown of checkbox items over the agent's column catalog, and an
 * Export CSV action that calls the export the view hands it and saves the
 * file. The ticked set is kept per view in local storage; a short set is
 * on by default and the rest wait to be ticked.
 */
import type { BlobResult } from '@optum/api-client/client';
import { DEFAULT_EXPORT_COLUMNS, EXPORT_COLUMNS } from '@optum/api-client/endpoints/cost';
import { useState } from 'react';

import { Button } from '../../ui/Button.js';
import {
  Dropdown,
  DropdownCheckboxItem,
  DropdownDivider,
  DropdownSection,
} from '../../ui/Dropdown.js';
import { useToast } from '../../ui/useToast.js';
import { downloadBlob } from '../../lib/download.js';

const STORAGE_PREFIX = 'optum.cost-export.';
const QUANTITIES = 'quantities';
const KNOWN = new Set(EXPORT_COLUMNS.map((column) => column.name));

export interface ExportMenuProps {
  /** Names the view, so each view remembers its own ticked set. */
  storageKey: string;
  /** The export to run with the ticked column names, in catalog order. */
  run: (columns: readonly string[]) => Promise<BlobResult>;
}

/** The remembered set for a view, or null when none is stored or storage is unavailable. */
export function readStoredColumns(storageKey: string): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((name): name is string => typeof name === 'string' && KNOWN.has(name));
  } catch {
    return null;
  }
}

function writeStoredColumns(storageKey: string, names: readonly string[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(names));
  } catch {
    // Storage can be blocked or full; the menu still works for this visit.
  }
}

/** The names in the catalog's order, whatever order they were ticked in. */
export function inCatalogOrder(names: readonly string[]): string[] {
  const picked = new Set(names);
  return EXPORT_COLUMNS.filter((column) => picked.has(column.name)).map((column) => column.name);
}

export function ExportMenu({ storageKey, run }: ExportMenuProps) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<string[]>(
    () => readStoredColumns(storageKey) ?? [...DEFAULT_EXPORT_COLUMNS],
  );
  const ordered = inCatalogOrder(picked);

  function update(next: readonly string[]) {
    const names = inCatalogOrder(next);
    setPicked(names);
    writeStoredColumns(storageKey, names);
  }

  async function exportNow() {
    setBusy(true);
    try {
      const { blob, filename } = await run(ordered);
      downloadBlob(blob, filename);
      setOpen(false);
    } catch (error) {
      toast.error('Export failed', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      width={300}
      align="end"
      trigger={<Button variant="secondary">Export</Button>}
    >
      <DropdownSection label="Columns" />
      {EXPORT_COLUMNS.map((column) => (
        <DropdownCheckboxItem
          key={column.name}
          label={column.header}
          sub={column.name === QUANTITIES ? 'one column per unit' : undefined}
          checked={picked.includes(column.name)}
          onCheckedChange={(on) =>
            update(on ? [...picked, column.name] : picked.filter((name) => name !== column.name))
          }
        />
      ))}
      <DropdownDivider />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 7,
          padding: '5px 5px 2px',
        }}
      >
        <span style={{ display: 'flex', gap: 5 }}>
          <Button variant="ghost" onClick={() => update(DEFAULT_EXPORT_COLUMNS)}>
            Default
          </Button>
          <Button variant="ghost" onClick={() => update(EXPORT_COLUMNS.map((c) => c.name))}>
            Select all
          </Button>
        </span>
        <Button
          variant="primary"
          disabled={busy || ordered.length === 0}
          onClick={() => void exportNow()}
        >
          Export CSV
        </Button>
      </div>
    </Dropdown>
  );
}
